import React, { useEffect, useRef, useState } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { ArrowRight } from '../components/Icons';
import DownloadButton from '../components/DownloadButton';
import { Flame, Glyph, PriceLines, Waveform } from '../components/PrometheusArt';
import useReveal from '../hooks/useReveal';
import useInstaller from '../hooks/useInstaller';
import { PROMETHEUS_VERSION } from '../site';

const SHOT = (name) => `/img/prometheus/${name}.webp`;

const RUN = [
  ['Searched Gmail', '14 threads from Acme'],
  ['Checked invoices.xlsx', '3 still open'],
  ['Drafted 3 replies', 'one per invoice'],
];

const MOMENTS = [
  {
    time: '08:00',
    tag: 'Routines',
    title: 'The day, laid out before you sit down.',
    body: 'Describe a job once: what to do, when to run and what to ask you first. A morning briefing can read your calendar and inbox and check your watchlist, so the day is on one page by eight. It sleeps through the quiet hours you set.',
    art: 'brief',
  },
  {
    time: '09:15',
    tag: 'Code',
    title: 'The bug, found and fixed. Applying it is your call.',
    body: 'Link a project folder and say what’s wrong. It reads the code, runs the tests and shows its fix as a diff you apply or throw away. A change its own critic rejects never reaches your files.',
    art: 'diff',
  },
  {
    time: '13:00',
    tag: 'Documents',
    title: 'A real spreadsheet, with the math checked.',
    body: 'Drop in a statement, a report or a spreadsheet and ask for the file you need. Word, Excel and PowerPoint files come back finished, with the layout and every formula checked before it calls them done.',
    art: 'sheet',
  },
  {
    time: '15:30',
    tag: 'Markets',
    title: 'Numbers you can trace.',
    body: 'Ask for a quote, a P/E or a side-by-side. It works the numbers in a Python sandbox on your PC, reads the pages behind them and cites every one.',
    art: 'chart',
  },
  {
    time: '19:00',
    tag: 'The web',
    title: 'Dinner, booked. Your card stays put.',
    body: 'It fills in forms and books through the site’s own buttons and fields. Paying, buying and deleting are refused, whatever it’s asked.',
    art: 'booking',
  },
  {
    time: 'Any time',
    tag: 'Voice',
    title: 'Or just say it.',
    body: 'With an xAI key saved, press the waveform and talk. Every spoken turn lands in the same chat, so the next thing you type picks up where you left off.',
    art: 'voice',
  },
];

const SHEET = [
  ['Rent', '1,850.00'],
  ['Groceries', '412.36'],
  ['Transport', '138.20'],
  ['Eating out', '264.90'],
];

const PERM_ROWS = [
  ['files', 'Edit files'],
  ['commands', 'Run commands'],
  ['reach', 'Mail, screen and web'],
];

const PERM_LABEL = {
  blocked: 'Blocked',
  asks: 'Asks first',
  auto: 'Your setting',
};

const MODES = [
  {
    key: 'ask',
    name: 'Ask',
    level: 'Answers',
    line: 'It answers. Nothing on your PC changes.',
    get: 'Answers from your question, a linked folder, market data and the web. Every new chat starts here.',
    perms: { files: 'blocked', commands: 'blocked', reach: 'blocked' },
  },
  {
    key: 'plan',
    name: 'Plan',
    level: 'Thinks first',
    line: 'It thinks the job through, shows you every step, then stops.',
    get: 'A plan naming the files and commands it would use, for you to read before anything happens.',
    perms: { files: 'blocked', commands: 'blocked', reach: 'blocked' },
  },
  {
    key: 'architect',
    name: 'Architect',
    level: 'Proposes',
    line: 'It writes the change up and leaves your project alone.',
    get: 'A saved proposal. Nothing reaches your project until you send it to the editor.',
    perms: { files: 'blocked', commands: 'blocked', reach: 'blocked' },
  },
  {
    key: 'draft',
    name: 'Draft',
    level: 'Asks each time',
    line: 'It prepares each action, then waits for your yes.',
    get: 'Edits, commands, mail and clicks, lined up for you one at a time.',
    perms: { files: 'asks', commands: 'asks', reach: 'asks' },
  },
  {
    key: 'agent',
    name: 'Agent',
    level: 'Does the work',
    line: 'It gets on with it, inside the limits you set.',
    get: 'Finished work, within your autonomy setting and spend cap. Sending, deleting and clicking still wait for you.',
    perms: { files: 'auto', commands: 'auto', reach: 'asks' },
    foot: 'Shell commands stay off until you turn them on. Paying, buying and deleting on websites are refused in every mode.',
  },
];

const STAYS = [
  'Your chats, and what it remembers',
  'API keys, in an encrypted store',
  'Your agents and their routines',
  'Usage, counted by day',
  'Local models you download',
];

const LEAVES = [
  'Your messages, to the cloud model you picked',
  'Web lookups you ask for',
  'Gmail and Calendar, once you connect them',
];

const LIMITS = [
  ['Refused', 'It won’t pay, buy or delete on a website. The app blocks it, whatever the prompt says.'],
  ['Asks first', 'Sending or deleting mail, and clicking or typing on your screen, wait for your yes.'],
  ['Off until you say', 'Shell commands and desktop control stay off until you switch them on.'],
  ['Fenced', 'It reaches public websites, your linked project and the folders you grant, read-only or read-write.'],
  ['Capped', 'A run that grows too large stops itself. Routines wait out your quiet hours.'],
  ['Checked', 'A code change its critic rejects never touches your files. A document that fails its checks isn’t finished.'],
];

const STEPS = [
  ['Run the installer', 'It installs for your Windows account, with no admin rights. A newer installer later updates the same copy.'],
  ['Allow WebView2 if asked', 'Windows uses it to draw the Prometheus window.'],
  ['Add a key or a model', 'Paste an OpenAI or xAI key in Settings, or download a suggested local model.'],
  ['Hand it something', 'New chats start in Ask, so nothing on your PC changes until you pick a mode that can.'],
];

const FAQ = [
  {
    q: 'What is Prometheus?',
    a: 'A desktop assistant from QSI Labs. It runs as a native Windows app and handles everyday questions, coding in a linked project, market lookups, Google Calendar and Gmail, and custom agents for recurring jobs.',
  },
  {
    q: 'Does my data leave my computer?',
    a: 'Chats, memory, keys and agent state are stored on your PC. With a cloud model, each conversation goes to that provider, OpenAI or xAI, to get a reply. With a downloaded local model it stays on the machine. Web lookups and connected services such as Gmail reach the internet when you use them.',
  },
  {
    q: 'Which models can it use?',
    a: 'OpenAI and xAI models with your own API key, or a local model downloaded from Settings: SmolLM2, Llama 3.2, Qwen2.5, Gemma 2 and larger mid-size options.',
  },
  {
    q: 'Can it run commands or click around on its own?',
    a: 'Only if you let it. Shell commands are off in the installed app until you set PROMETHEUS_ENABLE_SHELL=1, desktop control stays off until you arm it, and clicks, typing, sending and deleting still ask first.',
  },
  {
    q: 'Why does Windows warn me about the installer?',
    a: 'This build is not code-signed yet, so SmartScreen may name an unknown publisher. Choose More info, then Run anyway. Antivirus tools sometimes flag the bundled assistant runtime and its desktop automation for the same reason.',
  },
  {
    q: 'How do updates work?',
    a: `Installed copies look for new releases automatically. When a version newer than ${PROMETHEUS_VERSION} is published, the app downloads it and installs over the copy you have, in the same folder.`,
  },
  {
    q: 'What do I need?',
    a: 'Windows 10 or later on x64 or ARM64, the WebView2 runtime (setup offers it if missing), and either an API key or a downloaded local model.',
  },
];

function Head({ eyebrow, title, deck }) {
  return (
    <header className="pm-head">
      <p className="pm-eye">{eyebrow}</p>
      <h2 data-reveal>{title}</h2>
      {deck ? (
        <p className="pm-head__deck" data-reveal style={{ '--reveal-delay': '80ms' }}>
          {deck}
        </p>
      ) : null}
    </header>
  );
}

function Tick({ wait = false }) {
  return <span className={`pm-tick${wait ? ' pm-tick--wait' : ''}`} aria-hidden="true" />;
}

function Desk() {
  const at = (i) => ({ '--d': `${700 + i * 550}ms` });
  return (
    <figure className="pm-desk">
      <figcaption className="visually-hidden">
        An example run in Draft mode: Prometheus finds three unpaid Acme invoices in Gmail, drafts a reminder for
        each, and waits for you to send them.
      </figcaption>
      <div className="pm-desk__stack" aria-hidden="true">
        <div className="pm-peek">
          <strong>Morning briefing</strong>
          <span>Done · 8:02</span>
        </div>
        <div className="pm-peek">
          <strong>Fix the date tests</strong>
          <span className="pm-peek__wait">Ready to apply</span>
        </div>
        <div className="pm-run">
          <div className="pm-run__head">
            <Flame ground={false} />
            <strong>Prometheus</strong>
            <span className="pm-chip">Draft</span>
            <span className="pm-run__time">9:41</span>
          </div>
          <p className="pm-run__ask">“Find the invoices Acme still owes us and draft a friendly nudge for each.”</p>
          <ol className="pm-run__steps">
            {RUN.map(([text, detail], i) => (
              <li key={text} style={at(i)}>
                <Tick />
                <span>{text}</span>
                <small>{detail}</small>
              </li>
            ))}
            <li className="pm-run__wait" style={at(RUN.length)}>
              <Tick wait />
              <span>Waiting for you</span>
            </li>
          </ol>
          <div className="pm-draft" style={at(RUN.length + 0.6)}>
            <p className="pm-draft__meta">
              <span>To Dana Ruiz</span>
              <span>Invoice 1042 · 1 of 3</span>
            </p>
            <p className="pm-draft__body">
              Hi Dana, a quick nudge on invoice 1042 from March. Could you let me know when it’s scheduled? Thank
              you!
            </p>
          </div>
          <div className="pm-run__acts" style={at(RUN.length + 1.1)}>
            <span className="pm-fake pm-fake--go">Send all 3</span>
            <span className="pm-fake">Review</span>
            <small>Nothing sends until you say so</small>
          </div>
        </div>
      </div>
    </figure>
  );
}

function Hero() {
  const installer = useInstaller();
  return (
    <section className="pm-hero theme-dark" id="top" data-nav-tone="dark">
      <div className="container pm-hero__grid">
        <div className="pm-hero__copy">
          <p className="pm-eye">Prometheus, from QSI Labs</p>
          <h1>
            <span className="visually-hidden">Prometheus. </span>
            Just does the work
          </h1>
          <p className="pm-hero__lede">
            Hand it the inbox, the spreadsheet, the failing test. Prometheus gets the job done on your own PC, and
            checks with you before anything it can’t take back.
          </p>
          <div className="pm-hero__ctas">
            <DownloadButton />
            <a className="btn btn--ghost btn--arrow" href="#work">
              See what it does
              <ArrowRight />
            </a>
          </div>
          <p className="pm-hero__fine">{installer.fine}</p>
        </div>
        <Desk />
      </div>
    </section>
  );
}

function ArtHead({ icon, title, meta }) {
  return (
    <div className="pm-art__head">
      {icon === 'flame' ? <Flame ground={false} /> : <Glyph name={icon} />}
      <strong>{title}</strong>
      {meta ? <small>{meta}</small> : null}
    </div>
  );
}

function Art({ kind }) {
  if (kind === 'brief') {
    return (
      <div className="pm-art">
        <ArtHead icon="flame" title="Morning briefing" meta="Tue · 8:02" />
        <dl className="pm-art__rows">
          <div>
            <dt>Today</dt>
            <dd>3 meetings. Northwind at 10:00 comes first.</dd>
          </div>
          <div>
            <dt>Needs you</dt>
            <dd>Northwind’s contract. Sam asked to meet Thursday instead.</dd>
          </div>
          <div>
            <dt>Watchlist</dt>
            <dd className="pm-watch">
              <span>
                AAPL <em className="pm-up">▲</em>
              </span>
              <span>
                MSFT <em className="pm-down">▼</em>
              </span>
              <span>
                NVDA <em className="pm-up">▲</em>
              </span>
            </dd>
          </div>
        </dl>
        <div className="pm-art__foot">
          <small>Next run tomorrow, 8:00</small>
          <small>Quiet hours 22:00–07:00</small>
        </div>
      </div>
    );
  }

  if (kind === 'diff') {
    return (
      <div className="pm-art">
        <ArtHead icon="terminal" title="src/dates.py" meta="Proposed fix" />
        <pre className="pm-diff">
          <span>
            <em>40</em>
            {'  def parse_day(value):'}
          </span>
          <span className="pm-diff__del">
            <em>41</em>
            {'-     return datetime.strptime(value, DAY)'}
          </span>
          <span className="pm-diff__add">
            <em>41</em>
            {'+     day = datetime.strptime(value, DAY)'}
          </span>
          <span className="pm-diff__add">
            <em>42</em>
            {'+     return day.replace(tzinfo=timezone.utc)'}
          </span>
        </pre>
        <p className="pm-art__ok">
          <Tick />
          48 tests pass. 2 were failing.
        </p>
        <div className="pm-art__foot">
          <span className="pm-fake pm-fake--go">Apply</span>
          <span className="pm-fake">Discard</span>
          <small>Untouched until you apply</small>
        </div>
      </div>
    );
  }

  if (kind === 'sheet') {
    return (
      <div className="pm-art">
        <ArtHead icon="files" title="budget.xlsx" meta="From statement.pdf" />
        <table className="pm-sheet">
          <thead>
            <tr>
              <th>Category</th>
              <th>March</th>
            </tr>
          </thead>
          <tbody>
            {SHEET.map(([category, amount]) => (
              <tr key={category}>
                <td>{category}</td>
                <td>{amount}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td>2,665.46</td>
            </tr>
          </tfoot>
        </table>
        <p className="pm-art__ok">
          <Tick />
          Every formula checked. The total matches the statement.
        </p>
      </div>
    );
  }

  if (kind === 'chart') {
    return (
      <div className="pm-art">
        <ArtHead icon="markets" title="Apple and Microsoft, five years" meta="Python sandbox" />
        <div className="pm-art__chart">
          <PriceLines />
          <p className="pm-legend">
            <span className="pm-legend__a">Apple</span>
            <span className="pm-legend__b">Microsoft</span>
          </p>
        </div>
        <div className="pm-art__foot">
          <small>Sources</small>
          <span className="pm-src">apple.com</span>
          <span className="pm-src">microsoft.com</span>
          <span className="pm-src">+2</span>
        </div>
      </div>
    );
  }

  if (kind === 'booking') {
    return (
      <div className="pm-art">
        <ArtHead icon="browser" title="Table for four" meta="On the restaurant’s site" />
        <dl className="pm-art__rows">
          <div>
            <dt>When</dt>
            <dd>Friday, 7:30 pm</dd>
          </div>
          <div>
            <dt>Party</dt>
            <dd>4 people</dd>
          </div>
          <div>
            <dt>Name</dt>
            <dd>Alex Morgan</dd>
          </div>
        </dl>
        <p className="pm-art__ok">
          <Tick />
          Booked. The confirmation is in your inbox.
        </p>
        <div className="pm-art__foot">
          <small>Paying, buying and deleting: refused</small>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-art">
      <ArtHead icon="voice" title="Voice" meta="Listening" />
      <div className="pm-art__voice">
        <Waveform />
        <p>“Move my three o’clock with Sam to Thursday, and let him know.”</p>
      </div>
      <p className="pm-art__ok">
        <Tick />
        Moved to Thursday, 3:00 pm. Your note to Sam is waiting for you.
      </p>
    </div>
  );
}

function Work() {
  return (
    <section className="pm-sec pm-work" id="work" data-nav-tone="light">
      <div className="container">
        <Head
          eyebrow="What it does"
          title="From the first email to the last errand."
          deck="One ordinary weekday. Everything in it is something Prometheus does on a Windows PC today."
        />
        <ol className="pm-day">
          {MOMENTS.map((moment) => (
            <li key={moment.tag} className="pm-moment">
              <p className="pm-moment__time">
                {moment.time}
                <span>{moment.tag}</span>
              </p>
              <div className="pm-moment__copy" data-reveal>
                <h3>{moment.title}</h3>
                <p>{moment.body}</p>
              </div>
              <figure className="pm-moment__art" aria-hidden="true" data-reveal style={{ '--reveal-delay': '120ms' }}>
                <Art kind={moment.art} />
              </figure>
            </li>
          ))}
        </ol>
        <p className="pm-also">
          <span>Also in the box</span>A memory you can see in Settings, Word, Excel and PDF attachments, plugins you
          approve one tool at a time, helper agents for bigger goals, and slash commands for the things you do every
          day.
        </p>
      </div>
    </section>
  );
}

function Modes() {
  const [selected, setSelected] = useState(0);
  const stops = useRef([]);
  const mode = MODES[selected];

  const onKey = (event) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
    let next = null;
    if (step) next = (selected + step + MODES.length) % MODES.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = MODES.length - 1;
    if (next === null) return;
    event.preventDefault();
    setSelected(next);
    stops.current[next]?.focus();
  };

  return (
    <section className="pm-sec pm-modes" id="modes" data-nav-tone="light">
      <div className="container">
        <Head
          eyebrow="Five modes"
          title="Trust it a little. Then a little more."
          deck="Every new chat starts in Ask, where it can only answer. Turn it up when you’re ready. The app enforces each mode itself, so no prompt can talk its way past one."
        />
        <div className="pm-dial theme-dark" data-reveal>
          <p className="pm-dial__ends" aria-hidden="true">
            <span>Answers only</span>
            <span>Does the work</span>
          </p>
          <div
            className="pm-dial__track"
            role="tablist"
            aria-label="Modes"
            style={{ '--pos': selected / (MODES.length - 1) }}
          >
            {MODES.map((item, i) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                id={`mode-tab-${item.key}`}
                aria-controls="mode-panel"
                aria-selected={selected === i}
                tabIndex={selected === i ? 0 : -1}
                className="pm-stop"
                data-passed={i < selected ? 'true' : undefined}
                ref={(node) => {
                  stops.current[i] = node;
                }}
                onClick={() => setSelected(i)}
                onKeyDown={onKey}
              >
                <span className="pm-stop__dot" aria-hidden="true" />
                <span className="pm-stop__name">{item.name}</span>
                <span className="pm-stop__level">{item.level}</span>
              </button>
            ))}
          </div>
          <div
            className="pm-dial__panel"
            id="mode-panel"
            role="tabpanel"
            aria-labelledby={`mode-tab-${mode.key}`}
            key={mode.key}
          >
            <div>
              <h3>{mode.name}</h3>
              <p className="pm-dial__line">{mode.line}</p>
              <p className="pm-dial__get">{mode.get}</p>
            </div>
            <ul className="pm-dial__perms">
              {PERM_ROWS.map(([key, label]) => (
                <li key={key} data-state={mode.perms[key]}>
                  <span>{label}</span>
                  <strong>{PERM_LABEL[mode.perms[key]]}</strong>
                </li>
              ))}
            </ul>
            {mode.foot ? <p className="pm-dial__foot">{mode.foot}</p> : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function Privacy() {
  return (
    <section className="pm-sec pm-privacy" id="privacy" data-nav-tone="light">
      <div className="container pm-privacy__grid">
        <div>
          <Head
            eyebrow="Privacy"
            title="It lives on your PC."
            deck="Your chats, your keys and your agents stay in your Windows profile. Use your own OpenAI or xAI key and your messages go to that model for a reply. Download a model that fits your PC instead, and the conversation never leaves the machine."
          />
          <div className="pm-flows" data-reveal>
            <div>
              <h3>Stays on this PC</h3>
              <ul className="pm-flows__stay">
                {STAYS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Goes out only when you ask</h3>
              <ul className="pm-flows__out">
                {LEAVES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <figure className="pm-shot" data-reveal style={{ '--reveal-delay': '120ms' }}>
          <img
            src={SHOT('models')}
            width="1826"
            height="1078"
            alt="The Available models list in Settings, with SmolLM2, Llama 3.2, Qwen2.5 and Gemma 2 ready to download."
            loading="lazy"
            decoding="async"
          />
          <figcaption>Settings suggests a local model that fits your PC. The smallest is 0.4 GB.</figcaption>
        </figure>
      </div>
    </section>
  );
}

function Limits() {
  return (
    <section className="pm-sec pm-limits theme-dark" id="limits" data-nav-tone="dark">
      <div className="container">
        <Head
          eyebrow="Why it’s built this way"
          title="Built the way we run trading systems."
          deck="At QSI Labs, automated systems trade real money, and each one works inside hard limits it can’t override. Prometheus answers to the same rule."
        />
        <ul className="pm-limits__list">
          {LIMITS.map(([tag, text], i) => (
            <li key={tag} data-reveal style={{ '--reveal-delay': `${(i % 3) * 70}ms` }}>
              <span>{tag}</span>
              <p>{text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Install() {
  const installer = useInstaller();
  return (
    <section className="pm-sec pm-install" id="install" data-nav-tone="light">
      <div className="container pm-install__grid">
        <div>
          <Head eyebrow="Install" title="Running in four steps." />
          <ol className="pm-steps">
            {STEPS.map(([title, text], i) => (
              <li key={title} data-reveal style={{ '--reveal-delay': `${i * 60}ms` }}>
                <span>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <aside className="pm-get theme-dark" data-reveal style={{ '--reveal-delay': '120ms' }}>
          <div className="pm-get__head">
            <Flame ground={false} />
            <div>
              <p className="pm-get__name">Prometheus</p>
              <p className="pm-get__ver">Version {PROMETHEUS_VERSION}</p>
            </div>
          </div>
          <dl className="pm-get__specs">
            <div>
              <dt>File</dt>
              <dd className="pm-get__file">{installer.ready && installer.name ? installer.name : 'Windows installer'}</dd>
            </div>
            <div>
              <dt>Platform</dt>
              <dd>{installer.platform}</dd>
            </div>
            <div>
              <dt>Needs</dt>
              <dd>WebView2, plus an API key or a local model</dd>
            </div>
            <div>
              <dt>Updates</dt>
              <dd>Automatic, in place</dd>
            </div>
          </dl>
          <DownloadButton className="pm-get__btn" />
          <p className="pm-get__note">
            Not code-signed yet. If SmartScreen names an unknown publisher, choose More info, then Run anyway.
          </p>
        </aside>
      </div>
    </section>
  );
}

function Questions() {
  return (
    <section className="pm-sec pm-faq-sec" id="faq" data-nav-tone="light">
      <div className="container pm-faq-sec__grid">
        <Head eyebrow="FAQ" title="Questions" />
        <div className="pm-faq">
          {FAQ.map(({ q, a }) => (
            <details key={q} className="pm-faq__item">
              <summary>
                {q}
                <span className="pm-faq__sign" aria-hidden="true" />
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Close() {
  const installer = useInstaller();
  return (
    <section className="pm-close theme-dark" data-nav-tone="dark">
      <div className="container pm-close__inner">
        <Flame className="pm-close__mark" ground={false} />
        <h2 data-reveal>Bring the fire home.</h2>
        <p className="pm-close__lede" data-reveal style={{ '--reveal-delay': '80ms' }}>
          Install Prometheus, add a key or a local model, and hand it your first job.
        </p>
        <div className="pm-close__action" data-reveal style={{ '--reveal-delay': '160ms' }}>
          <DownloadButton />
          <p>{installer.href ? installer.name : installer.fine}</p>
        </div>
      </div>
    </section>
  );
}

export default function PrometheusPage() {
  useReveal();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Prometheus | A desktop assistant that asks before it acts | QSI Labs';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <Nav variant="product" />
      <main id="main" className="pm">
        <Hero />
        <Work />
        <Modes />
        <Privacy />
        <Limits />
        <Install />
        <Questions />
        <Close />
      </main>
      <Footer />
    </>
  );
}
